import { Checkbox, Form, Skeleton, Typography } from "antd";
import { type FormInstance } from "antd/lib/form";
import isEmpty from "lodash.isempty";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { PaymentClient, type Product } from "@fitness-app/data-models/entities/Product";

import { useAppSelector } from "~/store/initializeStore";
import { usePaymentGateways } from "../hooks/usePaymentGateways";

export const getPaymentMethodsList = (
  paymentMethodsConfigurationForProduct?: Product["paymentsMethods"] | null,
  defaultPaymentMethods?: ("p24" | "blik" | "klarna")[],
): ("p24" | "blik" | "klarna")[] => {
  const paymentMethods: ("p24" | "blik" | "klarna")[] = [...(defaultPaymentMethods || [])];

  if (!defaultPaymentMethods && !paymentMethodsConfigurationForProduct) {
    return ["p24", "blik"];
  }

  if (paymentMethodsConfigurationForProduct) {
    if (paymentMethodsConfigurationForProduct.p24 === PaymentClient.STRIPE) {
      paymentMethods.push("p24");
    }

    if (paymentMethodsConfigurationForProduct.blik === PaymentClient.STRIPE) {
      paymentMethods.push("blik");
    }

    if (paymentMethodsConfigurationForProduct.klarna === PaymentClient.STRIPE) {
      paymentMethods.push("klarna");
    }
  }

  return paymentMethods;
};

export const getPaymentMethodsMap = (paymentsMethods: ("p24" | "blik" | "klarna")[] = []) => {
  const updatedPaymentsMethods: Product["paymentsMethods"] = {
    p24: false,
    blik: false,
    klarna: false,
    applePay: "STRIPE",
    googlePay: "STRIPE",
  };

  paymentsMethods.forEach((method) => {
    updatedPaymentsMethods[method] = "STRIPE";
  });

  return updatedPaymentsMethods;
};

export const PaymentMethodsField = ({
  form,
  enabled = true,
  priceMethodsOverride,
}: {
  form: FormInstance;
  enabled?: boolean;
  priceMethodsOverride?: ("p24" | "blik" | "klarna")[];
}) => {
  const { t } = useTranslation(["products", "settings"]);
  const integrationData = useAppSelector((store) => store.productsSettings.data);
  const hasStripeIntegration = !!integrationData?.stripeConnectedAccountId;

  // TODO: add useStripeAccountSettings hook
  const isStripePaymentMethodsLoading = false;

  const stripePaymentMethods = {
    capabilities: {
      blik: "active",
      p24: "active",
      klarna: "active",
    },
  };
  const { paymentMethodsConfigurationForProduct, isStripeEnabled } = usePaymentGateways();

  useEffect(() => {
    if (priceMethodsOverride) {
      setTimeout(() => {
        form.setFieldsValue({
          paymentsMethods: priceMethodsOverride,
        });
      }, 300);
    } else if (!isEmpty(paymentMethodsConfigurationForProduct)) {
      // delay to wait for the form item to be mounted
      setTimeout(() => {
        form.setFieldsValue({
          paymentsMethods: getPaymentMethodsList(paymentMethodsConfigurationForProduct),
        });
      }, 300);
    } else if (stripePaymentMethods && isStripeEnabled) {
      const hasBlik = stripePaymentMethods.capabilities.blik === "active";
      const hasP24 = stripePaymentMethods.capabilities.p24 === "active";

      const paymentsMethods: ("p24" | "blik" | "klarna")[] = [];

      if (hasBlik) {
        paymentsMethods.push("blik");
      }

      if (hasP24) {
        paymentsMethods.push("p24");
      }
      setTimeout(() => {
        form.setFieldsValue({
          paymentsMethods,
        });
      }, 300);
    }
  }, [paymentMethodsConfigurationForProduct, isStripeEnabled, priceMethodsOverride, stripePaymentMethods]);

  const paymentOptions = useMemo(() => {
    if (!stripePaymentMethods) {
      return [];
    }

    return [
      {
        label: t("settings:paymentGateway.stripe.paymentMethods.types.p24"),
        value: "p24",
        disabled: stripePaymentMethods.capabilities.p24 !== "active",
      },
      {
        label: t("settings:paymentGateway.stripe.paymentMethods.types.blik"),
        value: "blik",
        disabled: stripePaymentMethods.capabilities.blik !== "active",
      },
      {
        label: t("settings:paymentGateway.stripe.paymentMethods.types.klarna"),
        value: "klarna",
        disabled: stripePaymentMethods.capabilities.klarna !== "active",
      },
    ];
  }, [stripePaymentMethods]);

  if (isStripePaymentMethodsLoading && hasStripeIntegration) {
    return (
      <div className="mb-4 space-y-2">
        <Skeleton active />
      </div>
    );
  }

  // if (!hasStripeIntegration || !stripePaymentMethods || paymentOptions.length === 0) {
  //   return null;
  // }

  return (
    <div className="mb-4 space-y-2">
      <Form.Item name="paymentsMethods" label={t("productDetails.stripePaymentMethods")}>
        <Checkbox.Group disabled={!enabled || !isStripeEnabled} options={paymentOptions} />
      </Form.Item>

      <Link to="/products?tab=settings&integration=stripe">
        <Typography.Link>{t("productDetails.stripePaymentMethodsDesc")}</Typography.Link>
      </Link>
    </div>
  );
};
